import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { Bell, X, Trophy, Coins, Gift, AlertTriangle } from 'lucide-react';

const NotificationContext = createContext(null);

export const useNotifications = () => useContext(NotificationContext);

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [permission, setPermission] = useState('default');

  // Request browser notification permission
  useEffect(() => {
    if ('Notification' in window) {
      setPermission(Notification.permission);
    }
  }, []);

  const requestPermission = async () => {
    if ('Notification' in window && Notification.permission === 'default') {
      const result = await Notification.requestPermission();
      setPermission(result);
      return result === 'granted';
    }
    return permission === 'granted';
  };

  // Add notification
  const notify = useCallback((notification) => {
    const id = Date.now();
    const newNotification = {
      id,
      timestamp: new Date(),
      read: false,
      ...notification
    };

    setNotifications(prev => [newNotification, ...prev.slice(0, 49)]); // Keep max 50

    // Send browser notification if permitted
    if (permission === 'granted' && 'Notification' in window) {
      try {
        new Notification(notification.title, {
          body: notification.message,
          icon: '/favicon.ico',
          badge: '/favicon.ico',
          tag: notification.type || 'default',
          silent: notification.silent || false
        });
      } catch (e) {
        console.log('Browser notification failed:', e);
      }
    }

    // Auto-dismiss after 5 seconds if not persistent
    if (!notification.persistent) {
      setTimeout(() => {
        dismissNotification(id);
      }, 5000);
    }

    return id;
  }, [permission]);

  // Dismiss notification
  const dismissNotification = useCallback((id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  // Mark as read
  const markAsRead = useCallback((id) => {
    setNotifications(prev => prev.map(n => 
      n.id === id ? { ...n, read: true } : n
    ));
  }, []);

  // Mark all as read
  const markAllAsRead = useCallback(() => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  }, []);

  // Clear all
  const clearAll = useCallback(() => {
    setNotifications([]);
  }, []);

  // Helper methods for common notification types
  const notifyWin = useCallback((amount, game) => {
    notify({
      type: 'win',
      title: 'Big Win!',
      message: `You won ₹${amount.toLocaleString()} on ${game}!`,
      icon: 'trophy'
    });
  }, [notify]);

  const notifyBonus = useCallback((amount, type) => {
    notify({
      type: 'bonus',
      title: 'Bonus Credited!',
      message: `₹${amount.toLocaleString()} ${type} bonus added to your wallet!`,
      icon: 'gift'
    });
  }, [notify]);

  const notifyTournament = useCallback((tournament) => {
    notify({
      type: 'tournament',
      title: 'Tournament Starting!',
      message: `${tournament.name} is starting soon. Join now!`,
      icon: 'trophy',
      persistent: true
    });
  }, [notify]);

  const notifyDeposit = useCallback((amount) => {
    notify({
      type: 'deposit',
      title: 'Deposit Successful',
      message: `₹${amount.toLocaleString()} has been added to your wallet.`,
      icon: 'coins'
    });
  }, [notify]);

  const notifyWithdrawal = useCallback((status, amount) => {
    notify({
      type: 'withdrawal',
      title: status === 'approved' ? 'Withdrawal Approved' : 'Withdrawal Update',
      message: status === 'approved' 
        ? `Your withdrawal of ₹${amount.toLocaleString()} has been approved!`
        : `Your withdrawal request has been ${status}.`,
      icon: status === 'approved' ? 'coins' : 'alert'
    });
  }, [notify]);

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <NotificationContext.Provider value={{
      notifications,
      unreadCount,
      permission,
      requestPermission,
      notify,
      notifyWin,
      notifyBonus,
      notifyTournament,
      notifyDeposit,
      notifyWithdrawal,
      dismissNotification,
      markAsRead,
      markAllAsRead,
      clearAll
    }}>
      {children}
      <NotificationToasts notifications={notifications.filter(n => !n.read).slice(0, 3)} onDismiss={dismissNotification} />
    </NotificationContext.Provider>
  );
};

// Toast component for in-app notifications
const NotificationToasts = ({ notifications, onDismiss }) => {
  if (notifications.length === 0) return null;

  const getIcon = (type) => {
    switch (type) {
      case 'win': return <Trophy className="w-5 h-5 text-[#FFD600]" />;
      case 'bonus':
      case 'gift': return <Gift className="w-5 h-5 text-[#00E701]" />;
      case 'deposit':
      case 'coins': return <Coins className="w-5 h-5 text-[#00E701]" />;
      case 'alert': return <AlertTriangle className="w-5 h-5 text-[#FF3B30]" />;
      default: return <Bell className="w-5 h-5 text-[#007AFF]" />;
    }
  };

  return (
    <div className="fixed bottom-4 right-4 z-[100] space-y-2 max-w-sm">
      {notifications.map((notification) => (
        <div
          key={notification.id}
          className="bg-[#1A1B1F] border border-white/10 rounded-xl p-4 shadow-xl animate-slide-up flex items-start gap-3"
        >
          <div className="shrink-0 mt-0.5">
            {getIcon(notification.icon || notification.type)}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white font-medium text-sm">{notification.title}</p>
            <p className="text-[#A1A1AA] text-xs mt-0.5">{notification.message}</p>
          </div>
          <button
            onClick={() => onDismiss(notification.id)}
            className="shrink-0 text-white/40 hover:text-white"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ))}
    </div>
  );
};

export default NotificationProvider;
